import * as Yup from 'yup';
import React, { useCallback, useEffect } from 'react';
import { get } from 'api/node';
import PromotionForm from 'components/Forms/PromotionForm';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Stack } from '@koob/margaret';
import {
  SegmentedControlField,
  SelectField,
  TextField,
} from 'components/Fields';
import { useParams } from 'react-router-dom';
import { Formik, getIn, useField, useFormikContext } from 'formik';
import {
  arePromotionPeriodsValid,
  formatPromotionFormPayload,
} from '../Form/Utilities/ExperienceHelpers';
import { BenefitsField } from 'components/Fields/PromotionParamsField';
import { useSnack } from 'hooks';
import i18n from '../../../i18n';
import {
  createPromotionPeriodFactory,
  mapApiResponseToFormValues,
} from '../../../utils/promotions';
import { getPromotions } from 'api/node/promotions';
import { ProductTypeEnum, PromotionStateEnum } from 'utils';

const getOrganizationsLinked = ({ experienceId }) =>
  get(`/experiences/${experienceId}/organizations`);

let parametersTypesOptions;

const PromotionTypeField = ({ name, isReadOnly }) => {
  const { t } = useTranslation('promotionForm');
  const { id, promotionId } = useParams();
  const { values, initialValues } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;
  const promoId = id || promotionId;
  const initialType =
    getIn(initialValues, `${name}.kind`) ||
    getIn(initialValues, `${name}.type`);
  const canEditType = !promoId || !initialType;

  return (
    <div className="w-full">
      <div className="my-3 flex justify-between space-x-3">
        <SelectField
          disabled={!canEditType || isReadOnly}
          name={`${name}.kind`}
          options={parametersTypesOptions}
          label={t('promotionTypes.label')}
          testId={'benefit-kind'}
          required={isOperational}
          touchRequired={isOperational ? false : undefined}
        />
        <SegmentedControlField
          name="benefit.promoPerRoom"
          options={[
            { label: t('form.no', { ns: 'experiences' }), value: true },
            { label: t('form.yes', { ns: 'experiences' }), value: false },
          ]}
          label={t('promoPerPax')}
          required
          disabled={isReadOnly}
        />
      </div>
    </div>
  );
};

const AdultPaxField = ({ index, periodIndex, isOperational, isReadOnly }) => {
  const { t } = useTranslation('experiences');
  return (
    <Stack direction="row" gutterSize={4} className="my-5">
      <TextField
        type="number"
        step="1"
        min="1"
        name={`periods[${periodIndex}].paxRanges[${index}].paxRange[0]`}
        label={t('form.minimumPaxRange')}
        disabled
      />
      <TextField
        type="number"
        step="1"
        min="1"
        name={`periods[${periodIndex}].paxRanges[${index}].paxRange[1]`}
        label={t('form.maximumPaxRange')}
        disabled
      />
      <TextField
        type="number"
        min="0"
        name={`periods[${periodIndex}].paxRanges[${index}].price`}
        label={t('form.promotionPrice')}
        required={isOperational}
        touchRequired={isOperational ? false : undefined}
        disabled={isReadOnly}
      />
    </Stack>
  );
};

const ChildrenPaxField = ({
  index,
  periodIndex,
  isOperational,
  isReadOnly,
}) => {
  const { t } = useTranslation('experiences');
  return (
    <Stack direction="row" gutterSize={4} className="my-5">
      <TextField
        type="number"
        step="1"
        min="0"
        name={`periods[${periodIndex}].ageRanges[${index}].ageRange[0]`}
        label={t('form.minimumAgeRange')}
        disabled
      />
      <TextField
        type="number"
        step="1"
        min="0"
        name={`periods[${periodIndex}].ageRanges[${index}].ageRange[1]`}
        label={t('form.maximumAgeRange')}
        disabled
      />
      <TextField
        type="number"
        min="0"
        name={`periods[${periodIndex}].ageRanges[${index}].price`}
        label={t('form.promotionPrice')}
        required={isOperational}
        touchRequired={isOperational ? false : undefined}
        disabled={isReadOnly}
      />
    </Stack>
  );
};
const BenefitPerPax = ({
  promotionPeriod,
  periodIndex,
  isOperational,
  isReadOnly,
}) => {
  return (
    <>
      {(promotionPeriod.paxRanges ?? []).map((_, index) => (
        <AdultPaxField
          periodIndex={periodIndex}
          index={index}
          key={index}
          isOperational={isOperational}
          isReadOnly={isReadOnly}
        />
      ))}
      {(promotionPeriod.ageRanges ?? []).map((_, index) => (
        <ChildrenPaxField
          periodIndex={periodIndex}
          index={index}
          key={index}
          isOperational={isOperational}
          isReadOnly={isReadOnly}
        />
      ))}
    </>
  );
};

const BenefitParamsField = ({ currency, experiencePeriods, isReadOnly }) => {
  const { values, dirty } = useFormikContext();
  const isOperational = values?.state === PromotionStateEnum.OPERATIONAL;

  const [{ value: periods }, , { setValue: setPeriods }] = useField({
    name: 'periods',
  });

  const getKindSnakeCase = (kind) => {
    if (!kind) return '';
    return kind
      .replace('Benefits::', '')
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '');
  };

  const kindSnakeCase = getKindSnakeCase(values?.benefit?.kind?.value);

  const syncPeriodsWithExperiences = useCallback(
    (currentPeriods) =>
      (currentPeriods ?? []).map((period) => {
        const experienceMatch = (experiencePeriods ?? []).find((exp) => {
          const expStart = new Date(exp.startAt);
          const expEnd = new Date(exp.endAt);
          const periodStart = new Date(period.startAt);
          const periodEnd = new Date(period.endAt);
          return periodStart <= expEnd && periodEnd >= expStart;
        });

        if (!experienceMatch || values?.benefit?.promoPerRoom) {
          return { ...period, ageRanges: [], paxRanges: [] };
        }

        return {
          ...period,
          paxRanges: (experienceMatch.adultPax ?? []).map((pax, i) => ({
            paxRange: [...pax.paxRange],
            price: period.paxRanges?.[i]?.price ?? '',
          })),
          ageRanges: (experienceMatch.childrenPax ?? []).map((age, i) => ({
            ageRange: [...age.ageRange],
            price: period.ageRanges?.[i]?.price ?? '',
          })),
        };
      }),
    [experiencePeriods, values?.benefit?.promoPerRoom],
  );

  useEffect(() => {
    if (!dirty) return;
    const newPeriods = syncPeriodsWithExperiences(periods);
    if (JSON.stringify(newPeriods) !== JSON.stringify(periods)) {
      setPeriods(newPeriods);
    }
  }, [dirty, periods, setPeriods, syncPeriodsWithExperiences]);

  return (
    <>
      <PromotionTypeField name="benefit" isReadOnly={isReadOnly} />
      {values?.benefit?.kind ? (
        <>
          <BenefitsField
            currency={currency}
            name="benefit"
            type={kindSnakeCase}
            isReadOnly={isReadOnly}
          />
          {!values.benefit.promoPerRoom &&
            (periods || []).map((period, index) => (
              <div className="p-8 m-8 border rounded-lg" key={index}>
                <span style={{ flex: '0 0 auto' }}>Period {index + 1}</span>
                <BenefitPerPax
                  promotionPeriod={period}
                  key={index}
                  periodIndex={index}
                  isOperational={isOperational}
                  isReadOnly={isReadOnly}
                />
              </div>
            ))}
        </>
      ) : null}
    </>
  );
};

const PromotionWizard = ({
  experience,
  promotion,
  handleSave,
  hasUnsavedData,
  setHasUnsavedData,
  isDuplicated,
  isReadOnly = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('promotionForm');
  const { experienceId } = useParams();
  const { notify } = useSnack();
  const { data } = useAsync({
    promiseFn: getOrganizationsLinked,
    experienceId: experienceId,
  });

  const { data: promotions } = useAsync({
    promiseFn: getPromotions,
    productId: experienceId,
    kind: ProductTypeEnum.EXPERIENCE,
    state: PromotionStateEnum.OPERATIONAL,
  });

  parametersTypesOptions = [
    {
      label: t('promotionTypes.amount'),
      value: 'Benefits::Amount',
    },
    {
      label: t('promotionTypes.percent'),
      value: 'Benefits::Percent',
    },
    {
      label: t('promotionTypes.supplement'),
      value: 'Benefits::Supplement',
    },
    {
      label: t('promotionTypes.supplement_percent'),
      value: 'Benefits::SupplementPercent',
    },
  ];

  const organizations = data?.data;
  const promos = Array.isArray(promotions?.data)
    ? promotions?.data
    : [promotions?.data].filter(Boolean);
  const promotionsThatCanBeLinked = (promos ?? []).filter(
    (elem) => elem?.id !== promotion?.id,
  );

  const EXPERIENCE_PERIOD_SCHEMA_VALIDATION = Yup.object().shape({
    startAt: Yup.date().required(i18n.t('errors:required')),
    endAt: Yup.date().required(i18n.t('errors:required')),
    days: Yup.object()
      .shape({
        Mo: Yup.bool().required(i18n.t('errors:required')),
        Tu: Yup.bool().required(i18n.t('errors:required')),
        We: Yup.bool().required(i18n.t('errors:required')),
        Th: Yup.bool().required(i18n.t('errors:required')),
        Fr: Yup.bool().required(i18n.t('errors:required')),
        Sa: Yup.bool().required(i18n.t('errors:required')),
        Su: Yup.bool().required(i18n.t('errors:required')),
      })
      .required(i18n.t('errors:required'))
      .test('days-is-valid', i18n.t('errors:required'), (value) => {
        return (
          Object.keys(value || {}).filter((key) => value[key] === true).length >
          0
        );
      }),
    paxRanges: Yup.array().of(
      Yup.object().shape({
        paxRange: Yup.array()
          .required(t('errors:required'))
          .min(1, t('errors:minLength', { length: 1 })),
        price: Yup.number()
          .typeError(t('errors:required'))
          .min(0, t('isPositive'))
          .test('is-required', t('errors:required'), function (value) {
            return value >= 0;
          }),
      }),
    ),
    ageRanges: Yup.array().of(
      Yup.object().shape({
        ageRange: Yup.array()
          .required(t('errors:required'))
          .min(1, t('errors:minLength', { length: 1 })),
        price: Yup.number()
          .typeError(t('errors:required'))
          .min(0, t('isPositive'))
          .test('is-required', t('errors:required'), function (value) {
            return value >= 0;
          }),
      }),
    ),
  });

  const BENEFIT_SCHEMA = Yup.object().shape({
    kind: Yup.object()
      .shape({
        value: Yup.mixed()
          .oneOf([
            'Benefits::Amount',
            'Benefits::Percent',
            'Benefits::Supplement',
            'Benefits::SupplementPercent',
          ])
          .required(t('errors:required')),
      })
      .required(t('errors:required')),
    value: Yup.number()
      .typeError(t('errors:required'))
      .required(t('errors:required'))
      .when('kind', {
        is: (kind) => kind?.value === 'Benefits::Percent',
        then: (schema) =>
          schema.max(100, t('errors:maxValueInclusive', { max: 100 })),
      }),
    unit: Yup.mixed()
      .oneOf(['per_night', 'per_night'])
      .required(t('errors:required')),
    promoPerRoom: Yup.boolean().required(t('errors:required')),
  });

  const VALIDATION_SCHEMA = Yup.object().shape({
    name: Yup.string().required(t('errors:required')),
    state: Yup.mixed().oneOf([
      PromotionStateEnum.DRAFT,
      PromotionStateEnum.OPERATIONAL,
      PromotionStateEnum.ARCHIVED,
    ]),
    periods: Yup.array().when('state', {
      is: PromotionStateEnum.OPERATIONAL,
      then: (schema) =>
        schema
          .of(EXPERIENCE_PERIOD_SCHEMA_VALIDATION)
          .required(t('errors:required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    cumulativePromotions: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required(),
      }),
    ),
    benefit: Yup.object().when('state', {
      is: PromotionStateEnum.OPERATIONAL,
      then: (schema) =>
        schema.concat(BENEFIT_SCHEMA).required(t('errors:required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    // An operational offer applies to the DMC or to at least one TO.
    organizations: Yup.array().when(['state', 'appliesToDmc'], {
      is: (state, appliesToDmc) =>
        state === PromotionStateEnum.OPERATIONAL && !appliesToDmc,
      then: (schema) =>
        schema
          .of(
            Yup.object().shape({
              id: Yup.string().required(),
            }),
          )
          .min(1, t('recipients.required'))
          .required(t('recipients.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const promotionInitialValues = {
    name:
      isDuplicated && promotion?.name
        ? `${promotion.name}${t('duplicateNameSuffix', { ns: 'promotions' })}`
        : (promotion?.name ?? ''),
    state: isDuplicated
      ? PromotionStateEnum.DRAFT
      : (promotion?.state ?? PromotionStateEnum.DRAFT),
    kind: promotion?.kind ?? 'basic_deal',
    experienceId: experience?.id ?? '',
    appliesToDmc: promotion?.appliesToDmc ?? false,
    periods: promotion
      ? (promotion?.availablePeriods || [])?.map((period) =>
          mapApiResponseToFormValues({
            id: period?.id,
            startAt: period.startAt,
            endAt: period.endAt,
            availableDays: period.days,
            ageRanges: period.ageRanges,
            paxRanges: period.paxRanges,
          }),
        )
      : [createPromotionPeriodFactory()],
    cumulativePromotions: promotion?.cumulativePromotions ?? [],
    benefit: {
      id: promotion?.benefit?.id,
      kind: parametersTypesOptions?.find(
        (item) => item?.value === promotion?.benefit?.kind,
      ),
      value: promotion?.benefit?.value ?? 0,
      currency: experience?.currency,
      promoPerRoom: promotion?.benefit?.promoPerRoom ?? true,
      unit: promotion?.benefit?.unit ?? 'per_night',
    },
    organizations:
      promotion?.organizations?.map((elem) => {
        return {
          id: elem.id,
          displayName: elem.displayName,
        };
      }) ?? [],
  };

  const handleSubmit = (values, methods) => {
    if (
      values.state !== PromotionStateEnum.OPERATIONAL ||
      arePromotionPeriodsValid({
        promotion: values,
        experience: experience,
      })
    ) {
      const formattedValues = formatPromotionFormPayload(values);
      return handleSave(formattedValues, methods);
    } else {
      notify('Invalid periods', { type: 'error' });
      return false;
    }
  };
  return (
    <Formik
      initialValues={promotionInitialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
    >
      {({ values, isSubmitting, touched, isValid, dirty, validateForm }) => (
        <PromotionForm
          setUnsavedData={setHasUnsavedData}
          toOrganizations={organizations}
          values={values}
          isSubmitting={isSubmitting}
          touched={touched}
          isValid={isValid}
          dirty={dirty}
          validateForm={validateForm}
          promotionsThatCanBeLinked={promotionsThatCanBeLinked}
          style={{
            gridTemplateAreas: `
          'top top'
          'deal deal'
          'period period'
          'promotionParams promotionParams'
          'cancellationPolicies cancellationPolicies'
        `,
            marginBottom: hasUnsavedData ? theme.spacing(3) : 0,
          }}
          promotionField={
            <BenefitParamsField
              currency={values?.benefit?.currency}
              experiencePeriods={experience?.periods}
              isReadOnly={isReadOnly}
            />
          }
          isDuplicate={isDuplicated}
          promotion={promotion}
          isReadOnly={isReadOnly}
        />
      )}
    </Formik>
  );
};

export default PromotionWizard;
